import React, { PureComponent, Fragment } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { withRouter } from "react-router-dom";
import { Button, withStyles, Box } from "@material-ui/core";
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import FormDialog from "../../../shared/components/FormDialog";
import ButtonCircularProgress from "../../../shared/components/ButtonCircularProgress";
import VisibilityPasswordTextField from "../../../shared/components/VisibilityPasswordTextField";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as AuthActions from "../../../redux/actions/auth";
import { TextField, Checkboxes, makeValidate, makeRequired, showErrorOnBlur } from 'mui-rff';
import styled from 'styled-components';
import sleep from './../../../shared/functions/sleep';
import * as yup from 'yup';
import api from 'shared/functions/api';
import passwordSetup from 'shared/functions/passwordSetup';
import * as SiteActions from "redux/actions/site";

const schema = yup.object().shape({
  email: yup.string().required().email(),
  password: yup.string().required(),
});
const validate = makeValidate(schema);
const required = makeRequired(schema);

const styles = (theme) => ({});

const ForgotButton = styled(Button)`
  font-family: Lato;
  font-size: 12px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.25;
  letter-spacing: normal;
  color: #b08f6e;
  text-transform: none;
  text-decoration: underline !important;
  margin-right: -8px;
`;

const CheckboxesSmallWrap = styled.div`
.MuiTypography-body1{ 
  font-size: 12px;
}`;

class LoginDialog extends PureComponent {

  state = {
    status: false,
  };

  componentDidMount() {
    this.setState({ status: this.props.status })

    this.fetchRegistrationMessages();
  }

  fetchRegistrationMessages() {
    api.get('/configs/registration-messages')
      .then((res) => {
        this.setState({ registrationMessages: res.data.fields });
      })
  }

  componentDidUpdate(prevProps) {
    if (prevProps.status !== this.props.status) {
      this.setState({ status: this.props.status })
    }
  }

  setStatus(status) {
    this.setState({ status })
  }

  openCustomPopupMessage = () => {
    // don't show before date
    const cutoff = new Date('2025-08-01');
    const date = new Date();
    if (date < cutoff) {
      return;
    }

    // if already accepted, don't show
    if(localStorage.getItem("diamonds-policy-notice-01-08-2025") === 'accepted') return;

    // show policy notice
    this.props.setOpenGeneralDialog({
      description: <div>
        <img src={'/images/diamonds-policy-notice-01-08-2025.png'} title="" alt="" style={{
          width: 'auto',
          maxWidth: '100%',
          imageRendering: 'auto',
        }} />
      </div>,
      submitLabel: 'Accept Policy',
      maxWidth: 'md',
      actionsStyle: {
        paddingBottom: 100
      },
      onComplete: () => {
        localStorage.setItem("diamonds-policy-notice-01-08-2025", "accepted");
        this.props.setOpenGeneralDialog(false);
      },
    });
  }

  onFormSubmit = async (data) => {
    const { history, after, onClose } = this.props;
    this.setStatus(null);

    await sleep(1000);

    try {
      await this.props.login(data.email, data.password, data.rememberMe, after);
      onClose();

      // open custom pop up message
      this.openCustomPopupMessage();

    } catch (err) {
      Array.isArray(err.response.data.errors.msg)
        ? this.setStatus({ [err.response.data.errors.msg[0].param]: err.response.data.errors.msg[0].msg })
        : err.response.data.errors.options && this.state.registrationMessages[err.response.data.errors.options.status]
          ? this.setStatus({ general: this.state.registrationMessages[err.response.data.errors.options.status], options: { customMessage: true } })
          : err.response.data.errors.options && err.response.data.errors.options.status
            ? this.setStatus({ general: err.response.data.errors.msg, options: err.response.data.errors.options })
            : this.setStatus({ general: err.response.data.errors.msg });
    }
  };

  render() {
    const { classes, onClose, initialValues, description, onBack,
      openChangePasswordDialog, forceEnableSubmitButton,
    } = this.props;
    const { status } = this.state;

    return (
      <Fragment>
        <FormDialog
          open
          status={status && status.general}
          statusOptions={status && status.options}
          onClose={onClose}
          validate={validate}
          initialValues={initialValues}
          onFormSubmit={(data) => this.onFormSubmit(data)}
          headline="Welcome"
          description={description || "You have previously registered with HENIG Diamonds, please enter your password to continue"}
          maxWidth="sm"
          content={({ submitting, values }) =>
            <Fragment>

              <TextField
                name="email"
                label="Email"
                margin="dense"
                // front end validation
                required={required.email}
                showError={showErrorOnBlur}
                // server side errors
                helperText={status && status.email}
                // inject only when needed, else it messes internal error prop
                {...{ [status && status.email && 'error']: status && status.email }}
              />

              <VisibilityPasswordTextField
                name="password"
                label="Password"
                margin="dense"
                autoComplete="off"
                // front end validation
                required={required.password}
                showError={showErrorOnBlur}
                // server side errors
                helperText={status && status.password}
                // inject only when needed, else it messes internal error prop
                {...{ [status && status.password && 'error']: status && status.password }}
              />

              <Box display="flex" justifyContent="space-between">
                <CheckboxesSmallWrap>
                  <Checkboxes
                    name="rememberMe"
                    data={[{ label: 'Remember me', value: 'y' }]}
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                  />
                </CheckboxesSmallWrap>

                <ForgotButton
                  disabled={submitting}
                  onClick={submitting ? null : () => openChangePasswordDialog({ initialValues: values, forceEnableSubmitButton: true })}
                >
                  Forgot Password?
                </ForgotButton>
              </Box>
            </Fragment>
          }
          actions={({ submitting, pristine }) =>
            <Fragment>
              <Button
                type="button"
                variant="contained"
                color="default"
                disabled={submitting}
                onClick={() => onBack ? onBack({ initialValues }) : onClose()}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                variant="contained"
                color="secondary"
                disabled={(submitting || pristine) && !forceEnableSubmitButton}
              >
                Continue
                {submitting && <ButtonCircularProgress />}
              </Button>
            </Fragment>
          }
        />
      </Fragment>
    );
  }
}

export default connect(null, (dispatch) =>
  bindActionCreators({ 
    ...AuthActions,
    ...SiteActions,
}, dispatch)
)(withRouter(withStyles(styles)(LoginDialog)));
